import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { companyRoutes } from 'constants/navigation';
import { useCoderbyteWorkspaceWithUsersQuery } from 'generated/graphql';
import { formatTimestamp } from 'helpers/date';
import { useNavigate } from 'react-router-dom';
import { Button, Drawer } from '@spotted-zebra-uk/ui-components';
import { UserStatusTag, WorkspaceStatusTag } from './Coderbyte.helpers';
import styles from './Coderbyte.module.scss';
import { AdminPasswordCard } from './components/AdminPasswordCard';
import { ApiKeyCard } from './components/ApiKeyCard';

type CoderbyteWorkspaceDrawerProps = {
  /** Company whose workspace to show, or null when the drawer is closed. */
  companyId: number | null;
  onClose: () => void;
};

/**
 * Everything about one company's Coderbyte workspace: its identifiers, both
 * credentials, and the people with accounts in it.
 *
 * Opened over the table rather than on its own route, matching the soft skills
 * screen. Which workspace is open lives in the URL as `?companyId=`, so the
 * drawer is still linkable — from the company's Integrations tab, from the
 * docs, or in a message to a colleague.
 */
export function CoderbyteWorkspaceDrawer({
  companyId,
  onClose,
}: CoderbyteWorkspaceDrawerProps) {
  const navigate = useNavigate();

  // Users sit behind a field resolver, so they are only fetched once a row is
  // actually opened rather than for every company in the table.
  const { data, loading, refetch } = useCoderbyteWorkspaceWithUsersQuery({
    variables: { companyId: companyId ?? 0 },
    skip: !companyId,
    fetchPolicy: 'cache-and-network',
  });

  const workspace = data?.coderbyteWorkspace ?? null;
  const users = workspace?.users ?? [];

  return (
    <Drawer
      open={!!companyId}
      onClose={onClose}
      title={workspace?.companyName ?? 'Coderbyte workspace'}
    >
      {workspace && (
        <div className={styles.drawer}>
          {/* Navigated rather than wrapped in links: `Button` renders a real
              <button>, and a button inside an anchor is invalid markup that
              swallows the click instead of following it. */}
          <div className={styles.drawerActions}>
            <Button
              variant="secondary"
              size="medium"
              onClick={() => {
                onClose();
                navigate(
                  `${companyRoutes.editCompany.url(
                    workspace.companyId
                  )}?activeTab=integrations`
                );
              }}
            >
              Company settings
            </Button>
            <Button
              variant="secondary"
              size="medium"
              onClick={() =>
                window.open(
                  workspace.dashboardUrl,
                  '_blank',
                  'noopener,noreferrer'
                )
              }
            >
              Open in Coderbyte
            </Button>
          </div>

          <div className={styles.drawerSection}>
            <InfoItem
              label="Status"
              value={<WorkspaceStatusTag status={workspace.status} />}
            />
            <InfoItem
              label="Organization ID"
              value={workspace.externalId}
              hasCopyButton
            />
            {/* No `valueNote` here: InfoItem lays the note out inline, and next
                to a copy button it runs underneath the icon. */}
            <InfoItem
              label="Coderbyte admin email"
              value={workspace.adminEmail}
              hasCopyButton
            />
            <p className={styles.caption}>
              Sign in to Coderbyte with the admin email and password below.
            </p>
          </div>

          <div className={styles.drawerSection}>
            <AdminPasswordCard
              companyId={workspace.companyId}
              workspace={workspace}
              onChanged={() => refetch()}
            />
            <ApiKeyCard
              companyId={workspace.companyId}
              workspace={workspace}
              onChanged={() => refetch()}
            />
          </div>

          <div className={styles.drawerSection}>
            <h3 className={styles.sectionTitle}>
              Users {!loading && `(${users.length})`}
            </h3>
            <p className={styles.caption}>
              Coderbyte allows one account per workspace, so each person is
              registered under a generated address rather than their own.
            </p>

            {!loading && users.length === 0 && (
              <p className={styles.emptyState}>
                Nobody from this company has opened Coderbyte yet. Accounts are
                created the first time someone crosses over.
              </p>
            )}

            {users.map(user => (
              <div key={user.id} className={styles.userRow}>
                <div className={styles.userHeader}>
                  <span className={styles.userName}>
                    {[user.firstName, user.lastName]
                      .filter(Boolean)
                      .join(' ') || `User ${user.userId}`}
                  </span>
                  <UserStatusTag status={user.status} />
                </div>
                <InfoItem label="Spotted Zebra email" value={user.userEmail} />
                <InfoItem
                  label="Coderbyte email"
                  value={user.coderbyteEmail}
                  hasCopyButton
                />
                <InfoItem
                  label="Last signed in"
                  value={formatTimestamp(user.lastLoginAt)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </Drawer>
  );
}

export default CoderbyteWorkspaceDrawer;
